import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Container } from './styled'
import { Box } from '../../shared_components/UI/Box'
import useUpcomingProjects from './useUpcomingProjects'
import { IDrop } from '../../types/reducerTypes'
import { isNotNil } from 'ramda'
import useFetchAndStoreAllDropsData from '../../hooks/drops/useFetchAndStoreAllDropsData'
import { useLazyGetAllDropsQuery } from '../../modules/listDrops/api'

import { DropStatus, getDropDateStatus } from '../DropDetails/helpers'
import {
  H2,
  H3,
  Span,
  Strong,
} from '../../shared_components/Library/Typeography'
import { useShouldShowForThreshold } from '../../theme'
import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'
import DropCard, { Description } from '../Drops/DropCard'
import { AppChip } from '../../shared_components/Library/Chip/AppChip'
import { OverflowingCarousel } from '../../shared_components/Library/OverflowingCarousel'
import { MemecoinCard, useMemecoins } from '../Memecoins/TokensList'
import useWeb3 from '../../hooks/useWeb3'
import { Divider, LabsCardBox } from '../../shared_components/Library/Card'
import Dotdotdot from 'react-dotdotdot'
import removeMd from 'remove-markdown'
import { usePageMaxWidth } from '../../shared_components/Page'
import { ProductsHero } from './HomeHero'

export const fadeSlideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

export const fadeSlideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

// Carousel indicator styles
export const CarouselIndicator = styled.span<{
  isActive: boolean
  invertColors: boolean
}>`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin: 0 5px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${({ isActive, invertColors, theme }) =>
    isActive
      ? invertColors
        ? '#080808'
        : 'white'
      : invertColors
      ? 'var(--surface-minimal-alpha-tertiary, rgba(0, 0, 0, 0.40))'
      : theme.semantics.zap.surface.minimalSecondary};

  transition: background-color 0.3s ease, border-color 0.3s ease;

  &:hover {
    background-color: ${({ invertColors, theme }) =>
      invertColors
        ? 'var(--surface-minimal-alpha-tertiary, rgba(0, 0, 0, 0.60))'
        : theme.semantics.zap.stroke.brand};
    border-color: ${({ invertColors, theme }) =>
      invertColors
        ? 'var(--surface-minimal-alpha-tertiary, rgba(0, 0, 0, 0.60))'
        : theme.semantics.zap.stroke.brand};
  }
`

export default function Home() {
  const { poolsData } = useWeb3()
  useEffect(() => {
    ;(async () => {
      await poolsData.dispatchPoolsToStore('labs')
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { pools: upcomingProjectsLabs, loading: loadingLabs } =
    useUpcomingProjects('labs')

  const [drops, setDrops] = useState<IDrop[]>([])

  const dispatchAllDropsToStore = useFetchAndStoreAllDropsData()

  useEffect(() => {
    dispatchAllDropsToStore({})
  }, [dispatchAllDropsToStore])

  const [fetchAllDrops, { isLoading }] = useLazyGetAllDropsQuery()

  useEffect(() => {
    fetchAllDrops({})
      .unwrap()
      .then(({ drops }) => {
        const groupedByParentDrop = drops.reduce((acc, drop) => {
          if (!drop.parentDropId) return acc
          acc[drop.parentDropId] = [...(acc?.[drop.parentDropId] ?? []), drop]
          return acc
        }, {}) as Record<number, IDrop[]>

        // Sort the drops within each group based on status and ID
        Object.keys(groupedByParentDrop).forEach((parentDropId) => {
          groupedByParentDrop[parentDropId] = groupedByParentDrop[parentDropId]
            .sort((a, b) => a.id - b.id)
            .map((drop, index) => ({ ...drop, chapterIndex: index }))

          groupedByParentDrop[parentDropId]
            .sort((a, b) => {
              const statusOrder = {
                [DropStatus.Live]: 0,
                [DropStatus.ComingSoon]: 1,
                [DropStatus.TBA]: 2,
                [DropStatus.Closed]: 3,
              }

              const statusA = getDropDateStatus(a)
              const statusB = getDropDateStatus(b)

              if (statusA === statusB) {
                return statusA === DropStatus.Closed ? b.id - a.id : a.id - b.id
              }

              return statusOrder[statusA] - statusOrder[statusB]
            })
            .map((drop, index) => ({ ...drop, chapterIndex: index }))
        })
        const latestDrops = Object.keys(groupedByParentDrop)
          .map((key) => {
            const dropsByParentDrop = groupedByParentDrop[Number(key)]
            return dropsByParentDrop[0] // The first drop after sorting will be the desired one
          })
          .sort((a, b) => {
            const aOrder = a?.parentDrop?.order
            const bOrder = b?.parentDrop?.order

            if (isNotNil(aOrder) && isNotNil(bOrder)) {
              // Both have order, compare by order
              return bOrder - aOrder
            } else if (isNotNil(aOrder)) {
              // Only a has order, sort a before b
              return 1
            } else if (isNotNil(bOrder)) {
              // Only b has order, sort b before a
              return -1
            }

            return 0
          })

        setDrops(latestDrops.reverse())
      })
  }, [fetchAllDrops])

  return (
    <Container>
      <Box dc padV="section">
        <ProductsHero />
      </Box>
      <ListDrops drops={drops} />
      {/* <ListMemecoins /> */}
      <ListLabsPools pools={upcomingProjectsLabs} />
    </Container>
  )
}

function ListDrops({ drops }: { drops: IDrop[] }) {
  const maxWidth = usePageMaxWidth()
  const isAtLeastExpandedPlusSize = useShouldShowForThreshold('expandedPlus')
  return (
    <Box dc padB="section">
      <Box ac g={8}>
        <AppChip type="drops" size="s" />
        <H2 s="L" fw="semibold">
          Latest airdrops
        </H2>
      </Box>
      <OverflowingCarousel
        g={32}
        padV="m"
        maxWidth={isAtLeastExpandedPlusSize ? maxWidth : undefined}
      >
        {drops?.slice?.()?.map((drop) => (
          <DropCard key={drop?.id} drop={drop} />
        ))}
      </OverflowingCarousel>
    </Box>
  )
}

// function ListMemecoins() {
//   const { memecoins } = useMemecoins({})
//   const maxWidth = usePageMaxWidth()
//   const isAtLeastExpandedPlusSize = useShouldShowForThreshold('expandedPlus')
//   return (
//     <Box dc padB="section">
//       <Box ac g={8}>
//         <AppChip type="launch" size="s" />
//         <H2 s="L" fw="semibold">
//           Trending tokens
//         </H2>
//       </Box>
//       <OverflowingCarousel
//         g={32}
//         padV="m"
//         maxWidth={isAtLeastExpandedPlusSize ? maxWidth : undefined}
//       >
//         {memecoins?.map((memecoin, index) => {
//           const key = memecoin?.poolAddress ?? Math.random() * Date.now()
//           return <MemecoinCard memecoin={memecoin} key={`${key}-${index}`} />
//         })}
//       </OverflowingCarousel>
//     </Box>
//   )
// }

function ListLabsPools({ pools }) {
  if (!pools.length) {
    return null
  }

  return (
    <Box dc padB="section">
      <Box ac g={8}>
        <AppChip type="labs" size="s" />
        <H2 s="L" fw="semibold">
          Upcoming raises
        </H2>
      </Box>
      <OverflowingCarousel g={32} padV="m">
        {(pools ?? []).map((pool, i) => {
          // so weird: Warning: Each child in a list should have a unique "key" prop.
          // this don't even fix it! anyone bored with too much time on their hands can have a look
          const id = uuidv4()
          const key = `${pool?.slug}-${i}-${id}`
          return <LabsCard key={key} pool={pool} />
        })}
      </OverflowingCarousel>
    </Box>
  )
}

export function LabsCard({ pool }) {
  const {
    cardImageSrc,
    coinName,
    coinTicker,
    logoUrl,
    descriptionText,
    stats,
    slug,
    isHardcoded,
  } = pool

  const Outer = ({ children }: { children: React.ReactNode }) =>
    isHardcoded ? (
      <>{children}</>
    ) : (
      <Link to={`/fundraise/${slug}`}>{children}</Link>
    )

  const desiredStatsOrder = ['Launch Price', 'Private price', 'Hard Cap']

  const sortedStats = stats
    .slice()
    .sort(
      (a: any, b: any) =>
        desiredStatsOrder.indexOf(a.name) - desiredStatsOrder.indexOf(b.name),
    )

  return (
    <Outer>
      <LabsCardBox>
        <img src={cardImageSrc} alt={coinName} />
        <Box dc g={4}>
          <H3 s="XL" fw="semibold">
            {coinName}
          </H3>
          <Box ac g={4}>
            <img
              src={logoUrl}
              className="token-logo"
              alt={`${coinName} logo`}
            />
            <Span c="text-brandPrimary" fw="semibold" s="L">
              {coinTicker}
            </Span>
          </Box>
        </Box>
        <Box fu>
          <Description>
            <Span s="M" fw="regular" c="text-secondary">
              <Dotdotdot clamp="66px">{removeMd(descriptionText)}</Dotdotdot>
            </Span>
          </Description>
        </Box>
        {sortedStats.map(({ name, value }, i: number) => (
          <>
            <Box key={`${name}-${i}-${value}-${pool.slug}`} ac jsb>
              <Span s="M" c="text-secondary">
                {name}
              </Span>
              <Box ac g={6} fu aw>
                <Strong s="M" uc>
                  {value}
                </Strong>
              </Box>
            </Box>
            {i !== stats.length - 1 && <Divider />}
          </>
        ))}
      </LabsCardBox>
    </Outer>
  )
}
